import { getBudgetCats, effAmt } from '@/utils';
/**
 * Контентный ключ категории: две категории с одинаковыми type+label+icon
 * считаются одной и той же, даже если у них разные id.
 *
 * Нужно потому, что import/sync может оставить в budget.categories дубликаты
 * одной категории с разными id (локальный DEFAULT_CAT id 'food' и серверный
 * uuid для тех же «Продукты»). Без этого на дашборде появлялась пустая
 * категория-близнец при включённой галочке «показать пустые».
 */
export const catContentKey = (c) => `${c.type} ${c.label} ${c.icon}`;
/**
 * Собирает статистику по категориям за период.
 *
 * Дедупликация идёт по КОНТЕНТУ (type+label+icon), а не по id: если в бюджете
 * оказалось две категории-близнеца с разными id, они схлопываются в одну
 * строку, а транзакции, ссылающиеся на любой из id, попадают в неё же.
 */
export function buildCatStats(budget, periodTxs) {
    const byContent = new Map();
    // id категории -> контентный ключ, чтобы резолвить tx.category в нужный bucket
    const idToKey = new Map();
    getBudgetCats(budget).forEach((c) => {
        const key = catContentKey(c);
        idToKey.set(c.id, key);
        if (!byContent.has(key)) {
            byContent.set(key, {
                id: c.id,
                label: c.label,
                icon: c.icon,
                type: c.type,
                planned: 0,
                actual: 0,
                transactions: [],
            });
        }
    });
    periodTxs.forEach((tx) => {
        if (tx.isStart)
            return;
        const rawKey = tx.category || `__none__${tx.type}`;
        // известная категория -> её контентный bucket; иначе отдельный bucket
        let key = idToKey.get(rawKey);
        if (!key) {
            key = rawKey;
            if (!byContent.has(key)) {
                byContent.set(key, {
                    id: rawKey,
                    label: 'Без категории',
                    icon: '📦',
                    type: tx.type,
                    planned: 0,
                    actual: 0,
                    transactions: [],
                });
            }
        }
        const s = byContent.get(key);
        s.transactions.push(tx);
        const e = effAmt(tx);
        if (e != null) {
            const sign = tx.fromSavings ? -1 : 1;
            if (tx.planned != null)
                s.planned += tx.planned * sign;
            if (tx.actual != null)
                s.actual += tx.actual * sign;
        }
    });
    return Array.from(byContent.values());
}
export function buildSections(catStats, savingsEnabled) {
    const types = (savingsEnabled ? ['income', 'expense', 'savings'] : ['income', 'expense']);
    return types.map((t) => {
        const cats = catStats.filter((c) => c.type === t);
        const totalPlanned = cats.reduce((s, c) => s + c.planned, 0);
        const totalActual = cats.reduce((s, c) => s + c.actual, 0);
        return { type: t, cats, totalPlanned, totalActual };
    });
}
/** Пустая ли категория (нет ни плана, ни факта за период). */
export const isEmptyCat = (c) => c.planned === 0 && c.actual === 0;
/** Сортировка категорий по пользовательскому порядку из localStorage. */
export function orderCats(cats, catOrder) {
    return [...cats].sort((a, b) => {
        const ia = catOrder.indexOf(a.id);
        const ib = catOrder.indexOf(b.id);
        if (ia === -1 && ib === -1)
            return 0;
        if (ia === -1)
            return 1;
        if (ib === -1)
            return -1;
        return ia - ib;
    });
}
/** Отсортированный список категорий с учётом фильтра «показывать пустые». */
export function visibleCats(cats, showEmpty, catOrder) {
    return orderCats(cats, catOrder).filter((c) => showEmpty || !isEmptyCat(c));
}
